/**
 * `country` — a searchable country list (`editors/country`: README *"A searchable country
 * list"*; the tier-2 capture opened a 130 × 48 in-cell affordance, so the list itself is our
 * own `PickerSearch` + option list, the same chrome every other picker uses).
 *
 * The vocabulary is `COUNTRY_LIST` in the shared folder, generated from ICU — the same names
 * monday stored for every code this account uses.
 */
import { COUNTRY_LIST, getColumnType, type CountrySettings, type CountryValue } from '@elizmonday/shared';
import { useState } from 'react';
import { CellPopover, PickerSearch } from '../primitives';
import { flagOf } from '../phone/index';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';
import { CountrySettingsDialog } from './SettingsDialog';

const shared = getColumnType('country');

export const COUNTRY_PICKER_WIDTH = 240;

export const countryColumnUi = defineColumnUi<CountryValue, CountrySettings>({
  type: 'country',
  icon: 'columnGeneric',
  gallery: { group: 'advanced', keywords: ['country', 'nation', 'iso', 'flag'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'left',
  groupable: true,
  /** P-263: the value is item-independent, so it may be written to a whole selection. */
  massEditable: true,

  Cell: ({ value }) => {
    if (value === null) return <span className="ez-cell__empty" aria-hidden="true" />;
    return (
      <span className="ez-cell__text" data-align="left" title={value.countryName}>
        <span className="ez-phone__flag" aria-hidden="true">
          {flagOf(value.countryCode)}
        </span>
        {value.countryName}
      </span>
    );
  },

  Editor: ({ value, initialInput, onCommit, onCancel }) => {
    const [query, setQuery] = useState(initialInput ?? '');
    const needle = query.trim().toLowerCase();
    const options = COUNTRY_LIST.filter(
      ([code, name]) => needle === '' || name.toLowerCase().includes(needle) || code.toLowerCase() === needle,
    ).slice(0, 60);
    return (
      <CellPopover
        label="Pick a country"
        width={COUNTRY_PICKER_WIDTH}
        /* **P-282**: the value is written by clicking an option; a dismiss writes nothing. */
        onDismiss={onCancel}
        onCancel={onCancel}
      >
        <PickerSearch value={query} onChange={setQuery} placeholder="Search countries" />
        <ul className="ez-option-list" role="listbox" aria-label="Countries">
          {options.map(([code, name]) => (
            <li key={code}>
              <button
                type="button"
                role="option"
                aria-selected={value?.countryCode === code}
                className="ez-option-list__option"
                data-testid={`country-option-${code}`}
                onClick={() => onCommit({ countryCode: code, countryName: name })}
              >
                <span className="ez-phone__flag" aria-hidden="true">
                  {flagOf(code)}
                </span>
                {name}
              </button>
            </li>
          ))}
          {options.length === 0 ? <li className="ez-option-list__empty">No matching country</li> : null}
        </ul>
      </CellPopover>
    );
  },

  SettingsDialog: CountrySettingsDialog,

  fromClipboard(text, settings) {
    const trimmed = text.trim();
    if (trimmed === '') return null;
    const match = COUNTRY_LIST.find(
      ([code, name]) => name.toLowerCase() === trimmed.toLowerCase() || code === trimmed.toUpperCase(),
    );
    if (match === undefined) throw new Error(`"${trimmed}" is not a country`);
    return shared.parseValue({ countryCode: match[0], countryName: match[1] }, settings) as CountryValue | null;
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('country'),
});
